import { useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks/use-permissions';
import { usePlan } from '../../hooks/use-plan';
import { hasFeature, FEATURE_DESCRIPTIONS } from '../../lib/config/pricing';
import type { PermissionCategory } from '../../lib/config/permissions';
import type { PlanFeatures } from '../../lib/config/pricing';
import { Lock, Crown } from 'lucide-react';
import { Button } from '../ui/button';
import { Link } from 'react-router-dom';

// Map routes to their required permissions
const ROUTE_PERMISSIONS: Record<string, PermissionCategory> = {
  '/': 'dashboard',
  '/dispatch': 'dispatch',
  '/loads': 'loads',
  '/drivers': 'drivers',
  '/fleet': 'fleet',
  '/customers': 'customers',
  '/carriers': 'carriers',
  '/invoices': 'invoices',
  '/settlements': 'settlements',
  '/compliance': 'compliance',
  '/documents': 'documents',
  '/reports': 'reports',
  '/analytics': 'analytics',
  '/settings': 'settings',
  '/messaging': 'messaging',
  '/tracking': 'tracking',
  '/marketplace': 'marketplace',
  '/integrations': 'integrations',
  '/ifta': 'ifta',
  '/collections': 'collections',
  '/lanes': 'lanes',
  '/margins': 'margins',
  '/edi': 'edi',
  '/portal': 'portal',
};

// Map routes to their required plan features
const ROUTE_FEATURES: Record<string, keyof PlanFeatures> = {
  '/messaging': 'inAppMessaging',
  '/margins': 'marginAnalysis',
  '/collections': 'collectionsManagement',
  '/edi': 'ediSupport',
  '/portal': 'customerLoginPortal',
};

// Some routes don't require any permission (public within app)
const PUBLIC_ROUTES = ['/login', '/register', '/confirmation-code', '/onboarding', '/help', '/profile', '/audit-log', '/maintenance'];

interface RouteGuardProps {
  children: React.ReactNode;
}

export function RouteGuard({ children }: RouteGuardProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const permissions = usePermissions();
  const { planType, planName, upgradePlan } = usePlan();

  // Get the base route (e.g., /loads/123 -> /loads)
  const baseRoute = '/' + (pathname.split('/')[1] || '');

  // Check if this is a public route
  if (PUBLIC_ROUTES.includes(baseRoute) || PUBLIC_ROUTES.includes(pathname)) {
    return <>{children}</>;
  }

  // Check role-based permission
  const requiredPermission = ROUTE_PERMISSIONS[baseRoute];
  if (requiredPermission && !permissions.canView(requiredPermission)) {
    return (
      <AccessDenied
        message={`You don't have access to ${requiredPermission}`}
        description={`Your role (${permissions.roleName}) doesn't have permission to view this section. Contact your administrator for access.`}
      />
    );
  }

  // Check plan-based feature
  const requiredFeature = ROUTE_FEATURES[baseRoute];
  if (requiredFeature) {
    const featureEnabled = hasFeature(planType || 'growth', requiredFeature);
    if (!featureEnabled) {
      const featureDescription = FEATURE_DESCRIPTIONS[requiredFeature] || 'This feature';
      return (
        <UpgradeRequired
          feature={featureDescription}
          currentPlan={planName || 'Growth'}
          upgradePlan={upgradePlan || undefined}
          isAdmin={permissions.isAdmin}
        />
      );
    }
  }

  return <>{children}</>;
}

interface AccessDeniedProps {
  message: string;
  description: string;
}

function AccessDenied({ message, description }: AccessDeniedProps) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      <div className="flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 text-destructive mb-4">
        <Lock className="h-8 w-8" />
      </div>
      <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
      <p className="text-lg text-muted-foreground mb-2">{message}</p>
      <p className="text-sm text-muted-foreground max-w-md mb-6">{description}</p>
      <Button asChild>
        <Link to="/">Return to Dashboard</Link>
      </Button>
    </div>
  );
}

interface UpgradeRequiredProps {
  feature: string;
  currentPlan: string;
  upgradePlan?: string;
  isAdmin: boolean;
}

function UpgradeRequired({ feature, currentPlan, upgradePlan, isAdmin }: UpgradeRequiredProps) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      <div className="flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 text-amber-600 mb-4">
        <Crown className="h-8 w-8" />
      </div>
      <h1 className="text-2xl font-bold mb-2">Upgrade Required</h1>
      <p className="text-lg text-muted-foreground mb-2">
        {feature} is not available on your current plan
      </p>
      <p className="text-sm text-muted-foreground max-w-md mb-6">
        You're on the <strong>{currentPlan}</strong> plan.
        {upgradePlan && ` Upgrade to ${upgradePlan} to unlock this feature.`}
      </p>
      <div className="flex gap-3">
        <Button variant="outline" asChild>
          <Link to="/">Return to Dashboard</Link>
        </Button>
        {isAdmin && (
          <Button asChild>
            <Link to="/settings/subscription/plans/">
              <Crown className="mr-2 h-4 w-4" />
              View Upgrade Options
            </Link>
          </Button>
        )}
      </div>
      {!isAdmin && (
        <p className="text-xs text-muted-foreground mt-4">
          Contact your administrator to upgrade your plan.
        </p>
      )}
    </div>
  );
}

/**
 * Hook to check if current route is accessible
 * Can be used for conditional rendering within pages
 */
export function useRouteAccess() {
  const location = useLocation();
  const pathname = location.pathname;
  const permissions = usePermissions();
  const { planType } = usePlan();

  const baseRoute = '/' + (pathname.split('/')[1] || '');

  // Check role permission
  const requiredPermission = ROUTE_PERMISSIONS[baseRoute];
  const hasRoleAccess = !requiredPermission || permissions.canView(requiredPermission);

  // Check if readonly
  const isReadonly = requiredPermission ? permissions.isReadonly(requiredPermission) : false;

  // Check plan feature
  const requiredFeature = ROUTE_FEATURES[baseRoute];
  // Feature check would need to be done at component level due to hooks rules

  return {
    hasRoleAccess,
    isReadonly,
    requiredPermission,
    requiredFeature,
  };
}
